/**
 * 合并 + 去重 planner（纯函数）。
 * 计算「该导入哪些分组、插入哪些条目」，由调用方（服务端 DB / 本地 IndexedDB）执行计划。
 *
 * 规则：
 * - 分组按 abbr upsert：已存在则复用、不改名，仅创建缺失的。
 * - 日清单按 date upsert：已存在则把条目合并进那天。
 * - 条目去重键 = date|content|hours|groupAbbr，命中（已存在或本文件内重复）则跳过。
 */
import type { DayNoteExport, ExportGroup, ExportItem } from './types';

export function itemDedupKey(
  date: string,
  content: string,
  hours: number | null,
  groupAbbr: string | null,
): string {
  return `${date} ${content.trim()} ${hours ?? ''} ${groupAbbr ?? ''}`;
}

/** 目标库的现状快照（调用方提供） */
export interface ExistingSnapshot {
  /** 已存在的分组简写（大小写按原样比较，建议调用方统一） */
  groupAbbrs: Set<string>;
  /** 已存在条目的去重键集合 */
  itemKeys: Set<string>;
}

export interface PlannedItem {
  date: string;
  item: ExportItem;
}

export interface MergePlan {
  groupsToCreate: ExportGroup[];
  itemsToInsert: PlannedItem[];
  summary: {
    groupsCreated: number;
    dayNotesTouched: number;
    itemsImported: number;
    itemsSkipped: number;
  };
}

export function planMerge(incoming: DayNoteExport, existing: ExistingSnapshot): MergePlan {
  const groupsToCreate: ExportGroup[] = [];
  const seenAbbr = new Set(existing.groupAbbrs);
  for (const g of incoming.data.groups) {
    if (!seenAbbr.has(g.abbr)) {
      groupsToCreate.push(g);
      seenAbbr.add(g.abbr);
    }
  }

  const itemsToInsert: PlannedItem[] = [];
  const seenKeys = new Set(existing.itemKeys);
  const touchedDates = new Set<string>();
  let skipped = 0;

  for (const dn of incoming.data.dayNotes) {
    for (const item of dn.items) {
      const key = itemDedupKey(dn.date, item.content, item.hours, item.groupAbbr);
      if (seenKeys.has(key)) {
        skipped += 1;
        continue;
      }
      seenKeys.add(key);
      itemsToInsert.push({ date: dn.date, item });
      touchedDates.add(dn.date);
    }
  }

  return {
    groupsToCreate,
    itemsToInsert,
    summary: {
      groupsCreated: groupsToCreate.length,
      dayNotesTouched: touchedDates.size,
      itemsImported: itemsToInsert.length,
      itemsSkipped: skipped,
    },
  };
}
